import { AxiosInstance } from 'axios';

import { ServiceBaseConfig } from './Interfaces/IAccountService';

export type IncorporationHistoryAction =
  | 'migrated_from_legacy'
  | 'pendency_opened'
  | 'document_sent'
  | 'step_advanced';

export interface IncorporationApiJurisdiction {
  id: number;
  name: string;
  slug: string;
}

export interface IncorporationApiStep {
  key: string;
  order: number;
  status: string;
  started_at: string | null;
  completed_at: string | null;
}

export interface IncorporationApiPendency {
  id: number;
  step: string;
  description: string;
  status: string;
  created_at: string;
  resolved_at: string | null;
  document: { original_name?: string | null } | null;
}

export interface IncorporationApiHistoryEntry {
  action: IncorporationHistoryAction | string;
  performed_by: string;
  details?: {
    legacy_status?: string;
    inferred_step?: string | null;
    pendency_id?: number;
    step?: string | null;
    description?: string;
    original_name?: string;
    from?: string | null;
    to?: string | null;
  } | null;
  created_at: string;
}

export interface IncorporationApiData {
  jurisdiction: IncorporationApiJurisdiction | null;
  current_step: string | null;
  completed_at: string | null;
  steps: IncorporationApiStep[];
  pendencies: IncorporationApiPendency[];
  history: IncorporationApiHistoryEntry[];
}

export interface IncorporationApiResponse {
  success: boolean;
  message?: string;
  data?: IncorporationApiData;
  error_code?: string;
}

const incorporationPath = (companyId: number | string) =>
  `/companies/${encodeURIComponent(String(companyId))}/incorporation`;

export class IncorporationService {
  public static async getTracking(
    api: AxiosInstance,
    companyId: number | string,
    config: ServiceBaseConfig
  ): Promise<IncorporationApiResponse> {
    const { data } = await api.get<IncorporationApiResponse>(incorporationPath(companyId), config);
    return data;
  }

  public static async uploadDocument(
    api: AxiosInstance,
    companyId: number | string,
    pendencyId: number,
    file: File,
    config: ServiceBaseConfig
  ): Promise<IncorporationApiResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<IncorporationApiResponse>(
      `${incorporationPath(companyId)}/pendencies/${encodeURIComponent(String(pendencyId))}/document`,
      formData,
      config
    );
    return data;
  }

  public static async advance(
    api: AxiosInstance,
    companyId: number | string,
    config: ServiceBaseConfig
  ): Promise<IncorporationApiResponse> {
    const { data } = await api.post<IncorporationApiResponse>(`${incorporationPath(companyId)}/advance`, undefined, config);
    return data;
  }

  public static async createPendency(
    api: AxiosInstance,
    companyId: number | string,
    description: string,
    config: ServiceBaseConfig
  ): Promise<IncorporationApiResponse> {
    const { data } = await api.post<IncorporationApiResponse>(
      `${incorporationPath(companyId)}/pendencies`,
      { description },
      config
    );
    return data;
  }
}
